/**
 * 타입-세이프 React Context 생성 유틸리티입니다.
 *
 * @remarks
 * Context 사용 시 undefined 체크를 생략할 수 있도록 도와줍니다.
 * Provider 없이 사용하면 명시적 에러를 발생시킵니다.
 *
 * @example
 * ```tsx
 * const [useMyContext, MyProvider] = createContextProvider<MyState>();
 *
 * // Provider 설정
 * <MyProvider value={myState}>{children}</MyProvider>
 *
 * // 사용 (undefined 체크 불필요)
 * const state = useMyContext();
 * ```
 */
import { createContext, useContext } from 'react';

type CreateCtx<A> = readonly [() => A, React.ProviderExoticComponent<React.ProviderProps<A | undefined>>];

/**
 * 타입-세이프한 Context와 Hook을 생성합니다.
 *
 * @typeParam A - Context 값의 타입
 * @param defaultValue - 선택적 기본값
 * @returns [useContext Hook, Provider 컴포넌트] 튜플
 */
export const createContextProvider = function <A>(defaultValue?: A): CreateCtx<A> {
  const ctx = createContext<A | undefined>(defaultValue);
  function useCtx(): A {
    const c = useContext(ctx);
    if (!c) throw new Error('useCtx must be inside a Provider with a value');
    return c;
  }
  // make TypeScript infer a tuple, not an array of union types
  return [useCtx, ctx.Provider as React.ProviderExoticComponent<React.ProviderProps<A | undefined>>] as const;
};
